"use client";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClockIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPathLabel } from "@/lib/activity-log-labels";
import type { LogEventViewModel } from "@/types/activity-log";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const PATH_BORDER_CLASS: Record<string, string> = {
  SYSTEM: "border-violet-400 text-violet-600",
  RETAIL: "border-blue-400 text-blue-600",
  AVS: "border-green-400 text-green-600",
};

const ACTION_BG_CLASS: Record<string, string> = {
  CONNECTION: "bg-blue-100 text-blue-800 border-blue-200",
  DISCONNECTION: "bg-red-100 text-red-800 border-red-200",
  KVPS_CHANGE: "bg-slate-100 text-slate-800 border-slate-200",
  VEHICLE_SEARCH_INIT: "bg-sky-100 text-sky-800 border-sky-200",
  DRAFT_CREATE: "bg-amber-100 text-amber-800 border-amber-200",
  DRAFT_UPDATE: "bg-yellow-100 text-yellow-800 border-yellow-200",
  DRAFT_DELETE: "bg-violet-100 text-violet-800 border-violet-200",
  CTM_VALIDATION: "bg-green-100 text-green-800 border-green-200",
};

const ACTION_NULL_CLASS = "bg-gray-100 text-gray-600 border-gray-200";

const DETAIL_PREVIEW_MAX = 40;
const REFERENCES_JOINED_MAX = 60;

function ActivityLogCard({
  item,
  onOpenDetail,
}: {
  item: LogEventViewModel;
  onOpenDetail: (id: number) => void;
}) {
  const pathClass =
    item.path != null
      ? (PATH_BORDER_CLASS[item.path] ?? "border-gray-400 text-gray-600")
      : "border-gray-400 text-gray-600";
  const actionClass =
    (item.actionType && ACTION_BG_CLASS[item.actionType]) ?? ACTION_NULL_CLASS;
  const detailsTruncated =
    item.detailsPreview && item.detailsPreview.length > DETAIL_PREVIEW_MAX
      ? `${item.detailsPreview.slice(0, DETAIL_PREVIEW_MAX)}…`
      : item.detailsPreview;

  const hasMultipleRefs =
    Array.isArray(item.targetRef) && item.targetRef.length > 0;
  const refsLabel = hasMultipleRefs ? "Références :" : "Référence :";
  const refsValue = hasMultipleRefs
    ? (() => {
        const joined = (item.targetRef as string[]).join(", ");
        return joined.length > REFERENCES_JOINED_MAX
          ? `${joined.slice(0, REFERENCES_JOINED_MAX)}…`
          : joined;
      })()
    : typeof item.targetRef === "string"
      ? item.targetRef
      : null;

  return (
    <Card className="rounded-xl border">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          {item.actionType != null && (
            <Badge className={cn("border font-medium", actionClass)}>
              {item.actionLabel}
            </Badge>
          )}
          {item.path != null && (
            <Badge
              variant="outline"
              className={cn(
                "rounded-full border-2 bg-transparent font-medium",
                pathClass,
              )}
            >
              {getPathLabel(item.path)}
            </Badge>
          )}
        </div>
        <div className="text-muted-foreground flex items-center gap-1 text-sm">
          <ClockIcon className="size-4" />
          <span>
            {format(new Date(item.createdAt), "yyyy-MM-dd HH:mm:ss", {
              locale: fr,
            })}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <div className="grid min-w-0 gap-x-4 gap-y-2 text-sm *:min-w-0 sm:grid-cols-3">
          <div className="min-w-0 overflow-hidden">
            <span className="text-muted-foreground">Utilisateur: </span>
            <span className="block truncate font-medium">
              {item.userDisplay}
            </span>
            {item.userSecondaryId && (
              <div className="text-muted-foreground mt-0.5 truncate text-xs">
                Identifiant utilisateur: {item.userSecondaryId}
              </div>
            )}
          </div>
          <div className="min-w-0 overflow-hidden">
            {item.kvps != null && (
              <>
                <span className="text-muted-foreground">KVPS: </span>
                <span className="block truncate font-medium">{item.kvps}</span>
              </>
            )}
            {refsValue != null && (
              <div className="text-muted-foreground mt-0.5 min-w-0 overflow-hidden">
                <span className="text-muted-foreground">{refsLabel} </span>
                <span
                  className="block truncate font-mono text-xs"
                  title={
                    hasMultipleRefs
                      ? (item.targetRef as string[]).join(", ")
                      : (refsValue ?? undefined)
                  }
                >
                  {refsValue}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 text-right">
            {item.result != null && (
              <>
                <span className="text-muted-foreground">Résultat: </span>
                <span className="block truncate font-medium">
                  {item.resultLabel}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="min-w-0 border-t pt-2">
          <span className="text-muted-foreground text-sm">Détails: </span>
          <button
            type="button"
            onClick={() => onOpenDetail(item.id)}
            className="text-primary hover:bg-muted focus:ring-ring inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-left text-sm hover:underline focus:ring-2 focus:outline-none"
            aria-label="Voir le détail de l’événement"
          >
            {detailsTruncated ? (
              <span className="truncate">{detailsTruncated}</span>
            ) : (
              <span className="text-muted-foreground">Voir le détail</span>
            )}
            <ChevronRightIcon className="size-4 shrink-0" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export { ActivityLogCard };
